import { useCallback, useRef } from "react";

/**
 * Hook umożliwiający ręczną zmianę szerokości kolumn w tabeli.
 *
 * Użycie:
 *   const { getResizeProps } = useColumnResize();
 *
 *   <th {...getResizeProps()}>
 *     Nazwa kolumny
 *   </th>
 *
 * Użytkownik łapie za prawą krawędź nagłówka i przeciąga, aby zmienić szerokość.
 * Działa na dowolnej tabeli z klasą .custom-table (resizable-column style w customTable.scss).
 */
const useColumnResize = () => {
  const resizeState = useRef(null);
  // Flaga informująca, że właśnie zakończono resize — blokuje click (sortowanie)
  const wasResizing = useRef(false);

  const handleMouseDown = useCallback((e) => {
    // Tylko prawa krawędź nagłówka (ostatnie 6px)
    const th = e.target.closest("th");
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const isRightEdge = e.clientX >= rect.right - 6;
    if (!isRightEdge) return;

    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = th.offsetWidth;

    // Wyłącz zaznaczanie tekstu podczas resize
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    resizeState.current = { th, startX, startWidth };

    const handleMouseMove = (moveEvent) => {
      if (!resizeState.current) return;
      const diff = moveEvent.clientX - resizeState.current.startX;
      const newWidth = Math.max(50, resizeState.current.startWidth + diff);
      resizeState.current.th.style.width = `${newWidth}px`;
      resizeState.current.th.style.minWidth = `${newWidth}px`;
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      // Ustaw flagę, aby zablokować nadchodzący click (sortowanie)
      wasResizing.current = true;
      resizeState.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Handler click — blokuje sortowanie jeśli właśnie zakończono resize
  const handleClick = useCallback((e) => {
    if (wasResizing.current) {
      e.stopPropagation();
      e.preventDefault();
      wasResizing.current = false;
    }
  }, []);

  const getResizeProps = useCallback(() => ({
    onMouseDown: handleMouseDown,
    onClickCapture: handleClick,
    className: "resizable-column",
  }), [handleMouseDown, handleClick]);

  return { getResizeProps };
};

export default useColumnResize;
